import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { 
  FileText, Sparkles, ArrowRight, Shield, Loader2, Star, 
  Upload, Palette, X, Flag, Plus, Trash2, Briefcase, GraduationCap, ChevronDown, ChevronUp
} from "lucide-react";
import { API, LOGO_URL, BRAND, packageNames, colorSchemes, layoutStyles } from "../common/constants";

const ResumeBuilder = ({ selectedPackage, onBack }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showWorkHistory, setShowWorkHistory] = useState(false);
  const [showEducation, setShowEducation] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [formData, setFormData] = useState({
    user_name: "",
    user_email: "",
    user_phone: "",
    linkedin_url: "",
    resume_text: "",
    job_description: "",
    special_instructions: "",
    include_cv: selectedPackage === "complete" || selectedPackage === "career_profiler",
    include_career_profiler: selectedPackage === "career_profiler",
    target_industry: "",
    preferred_location: "",
    color_scheme: "classic_black",
    layout_style: "single_column",
    // Structured work history
    work_history: [],
    // Structured education
    education: []
  });
  const [result, setResult] = useState(null);
  const [discountCode, setDiscountCode] = useState("");
  const [discountValidation, setDiscountValidation] = useState(null);
  const [validatingDiscount, setValidatingDiscount] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [extractingContact, setExtractingContact] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const extractContactInfo = async (resumeText) => {
    if (!resumeText || resumeText.length < 50) return;
    
    setExtractingContact(true);
    try {
      const response = await axios.post(`${API}/extract-contact`, { resume_text: resumeText });
      if (response.data.success && response.data.contact_info) {
        const info = response.data.contact_info;
        setFormData(prev => ({
          ...prev,
          user_name: info.name || prev.user_name,
          user_email: info.email || prev.user_email,
          user_phone: info.phone || prev.user_phone,
          linkedin_url: info.linkedin || prev.linkedin_url,
          preferred_location: info.location || prev.preferred_location
        }));
        toast.success("Contact info extracted from resume!");
      }
    } catch (error) {
      console.log("Contact extraction failed, user can enter manually");
    } finally {
      setExtractingContact(false);
    }
  };

  // Work History Management
  const addWorkEntry = () => {
    setFormData(prev => ({
      ...prev,
      work_history: [...prev.work_history, {
        id: Date.now(),
        job_title: "",
        company: "",
        location: "",
        start_date: "",
        end_date: "",
        bullets: ""
      }]
    }));
    setShowWorkHistory(true);
  };

  const updateWorkEntry = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      work_history: prev.work_history.map(w => w.id === id ? { ...w, [field]: value } : w)
    }));
  };

  const removeWorkEntry = (id) => {
    setFormData(prev => ({
      ...prev,
      work_history: prev.work_history.filter(w => w.id !== id)
    }));
  };

  // Education Management
  const addEducationEntry = () => {
    setFormData(prev => ({
      ...prev,
      education: [...prev.education, {
        id: Date.now(),
        degree: "",
        institution: "",
        location: "",
        year: ""
      }]
    }));
    setShowEducation(true);
  };

  const updateEducationEntry = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      education: prev.education.map(e => e.id === id ? { ...e, [field]: value } : e)
    }));
  };

  const removeEducationEntry = (id) => {
    setFormData(prev => ({
      ...prev,
      education: prev.education.filter(e => e.id !== id)
    }));
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload-resume`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setFormData(prev => ({ ...prev, resume_text: response.data.text }));
        setUploadedFileName(response.data.filename);
        toast.success(`Resume extracted from ${response.data.filename}`);
        extractContactInfo(response.data.text);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to extract. Try copy-paste instead.");
    } finally {
      setUploading(false);
    }
  };

  const handleOptimize = async () => {
    if (!formData.user_name || !formData.user_email || !formData.resume_text || !formData.job_description) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const includeCV = selectedPackage === "complete" || selectedPackage === "premium" || selectedPackage === "career_profiler";
      const includeCareerProfiler = selectedPackage === "career_profiler";
      
      const requestData = {
        ...formData,
        include_cv: includeCV,
        include_career_profiler: includeCareerProfiler
      };
      
      const response = await axios.post(`${API}/optimize`, requestData);
      // Now returns pending_session_id and preview data (not full documents)
      setResult({
        ...response.data,
        session_id: response.data.pending_session_id // Map for checkout compatibility
      });
      setStep(2);
      toast.success("Resume analyzed! Review your match score and proceed to checkout.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to analyze. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const validateDiscountCode = async () => {
    if (!discountCode.trim()) return;
    
    setValidatingDiscount(true);
    try {
      const response = await axios.get(`${API}/validate-discount`, {
        params: { discount_code: discountCode, package_type: selectedPackage }
      });
      setDiscountValidation(response.data);
      if (response.data.valid) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      setDiscountValidation({ valid: false, message: "Error validating code" });
      toast.error("Error validating code");
    } finally {
      setValidatingDiscount(false);
    }
  };

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const packageMap = { "basic": "basic", "complete": "premium", "career_profiler": "career_profiler" };
      const backendPackage = packageMap[selectedPackage] || selectedPackage;
      
      const response = await axios.post(`${API}/checkout`, {
        session_id: result.session_id,
        package_type: backendPackage,
        origin_url: window.location.origin,
        discount_code: discountValidation?.valid ? discountCode : null
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error("Failed to initiate checkout.");
    } finally {
      setLoading(false);
    }
  };

  const getPrice = () => {
    const prices = { basic: 9.99, complete: 19.99, career_profiler: 29.99 };
    const price = prices[selectedPackage] || 9.99;
    if (discountValidation?.valid) {
      const discounted = price * (1 - discountValidation.discount_percent / 100);
      return { original: price.toFixed(2), final: discounted.toFixed(2), hasDiscount: true, percent: discountValidation.discount_percent };
    }
    return { original: price.toFixed(2), final: price.toFixed(2), hasDiscount: false };
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-200">
        <div className="container-main flex items-center justify-between h-16">
          <button onClick={onBack} className="flex items-center gap-3">
            <img src={LOGO_URL} alt={BRAND.parent} className="h-10 w-auto rounded-md" />
            <span className="hidden sm:block text-sm font-semibold text-[#071734]">{BRAND.productSuite}</span>
          </button>
          
          <div className="flex items-center gap-4">
            <Badge variant="outline" className={`${selectedPackage === 'career_profiler' ? 'border-[#E86A33]/50 text-[#E86A33] bg-[#E86A33]/10' : 'border-[#00a3e0]/50 text-[#00a3e0]'}`}>
              {packageNames[selectedPackage]} Package
            </Badge>
            <div className="flex items-center gap-2">
              <div className={`px-3 py-1.5 rounded-md ${step >= 1 ? 'bg-[#00a3e0]/10 text-[#00a3e0]' : 'text-gray-400'}`}>
                <span className="text-sm">1. Input</span>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400" />
              <div className={`px-3 py-1.5 rounded-md ${step >= 2 ? 'bg-[#00a3e0]/10 text-[#00a3e0]' : 'text-gray-400'}`}>
                <span className="text-sm">2. Review & Pay</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="container-main py-8">
        {step === 1 && (
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Ethical Promise */}
            <Card className="bg-gradient-to-r from-[#00a3e0]/5 to-[#071734]/5 border-[#00a3e0]/30">
              <CardContent className="py-4">
                <div className="flex items-center gap-4">
                  <Shield className="w-10 h-10 text-[#00a3e0] flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-[#071734]">Our Ethical AI Promise</h3>
                    <p className="text-sm text-gray-600">We never fabricate information. Your resume will be optimized using only your real experience.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Military Link */}
            <Link 
              to="/veterans" 
              className="flex items-center justify-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600 hover:bg-red-100 transition-colors"
            >
              <Flag className="w-4 h-4" />
              We Honor Those Who Serve — Click here for exclusive discounts
            </Link>

            {/* Main Form */}
            <Card className="border-gray-200">
              <CardContent className="pt-6 space-y-6">
                {/* Contact Info Section */}
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-md space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium flex items-center gap-2 text-[#071734]">
                      <FileText className="w-4 h-4 text-[#00a3e0]" /> 
                      Contact Information
                      {(formData.user_name && formData.user_email) && (
                        <span className="text-xs text-[#00a3e0] ml-2">(auto-filled)</span>
                      )}
                    </h4>
                  </div>
                  <p className="text-xs text-gray-500">
                    Upload your resume and we'll auto-extract this info, or enter manually.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#071734]">Full Name *</Label>
                      <Input 
                        data-testid="input-name"
                        placeholder="John Doe"
                        value={formData.user_name}
                        onChange={(e) => handleInputChange("user_name", e.target.value)}
                        className="border-gray-300 focus:border-[#00a3e0] focus:ring-[#00a3e0]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#071734]">Email *</Label>
                      <Input 
                        data-testid="input-email"
                        type="email"
                        placeholder="john@example.com"
                        value={formData.user_email}
                        onChange={(e) => handleInputChange("user_email", e.target.value)}
                        className="border-gray-300 focus:border-[#00a3e0] focus:ring-[#00a3e0]"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#071734]">Phone Number</Label>
                      <Input 
                        data-testid="input-phone"
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={formData.user_phone}
                        onChange={(e) => handleInputChange("user_phone", e.target.value)}
                        className="border-gray-300 focus:border-[#00a3e0] focus:ring-[#00a3e0]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#071734]">Location (City, State)</Label>
                      <Input 
                        data-testid="input-location"
                        placeholder="Portland, OR"
                        value={formData.preferred_location}
                        onChange={(e) => handleInputChange("preferred_location", e.target.value)}
                        className="border-gray-300 focus:border-[#00a3e0] focus:ring-[#00a3e0]"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#071734]">LinkedIn Profile URL</Label>
                    <Input 
                      data-testid="input-linkedin"
                      placeholder="linkedin.com/in/johndoe"
                      value={formData.linkedin_url || ""}
                      onChange={(e) => handleInputChange("linkedin_url", e.target.value)}
                      className="border-gray-300 focus:border-[#00a3e0] focus:ring-[#00a3e0]"
                    />
                  </div>
                </div>

                {/* File Upload */}
                <div className="space-y-2">
                  <Label className="text-[#071734]">Your Resume *</Label>
                  <label className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-md cursor-pointer transition-colors ${uploading ? 'border-[#00a3e0]/50 bg-[#00a3e0]/5' : 'border-gray-300 hover:border-[#00a3e0]/50'}`}>
                    <input type="file" accept=".pdf,.docx,.doc" onChange={handleFileUpload} className="hidden" disabled={uploading} />
                    {uploading ? (
                      <><Loader2 className="w-5 h-5 animate-spin text-[#00a3e0]" /><span className="text-sm text-[#00a3e0]">Extracting...</span></>
                    ) : (
                      <><Upload className="w-5 h-5 text-gray-400" /><span className="text-sm text-gray-500">Upload PDF or Word document</span></>
                    )}
                  </label>
                  {uploadedFileName && (
                    <div className="flex items-center gap-2 p-2 bg-[#00a3e0]/10 rounded text-sm">
                      <FileText className="w-4 h-4 text-[#00a3e0]" />
                      <span className="flex-1 text-[#00a3e0]">{uploadedFileName}</span>
                      <button onClick={() => { setUploadedFileName(null); handleInputChange("resume_text", ""); }}>
                        <X className="w-4 h-4 text-[#00a3e0]" />
                      </button>
                    </div>
                  )}
                  {extractingContact && (
                    <div className="flex items-center gap-2 text-sm text-[#00a3e0]">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Extracting contact info from resume...</span>
                    </div>
                  )}
                  <Textarea 
                    data-testid="input-resume"
                    placeholder="Or paste your resume here..."
                    className="min-h-[150px] font-mono text-sm"
                    value={formData.resume_text}
                    onChange={(e) => { handleInputChange("resume_text", e.target.value); setUploadedFileName(null); }}
                  />
                  {formData.resume_text && !uploadedFileName && !extractingContact && (
                    <Button 
                      type="button" 
                      size="sm" 
                      variant="outline" 
                      className="mt-2"
                      onClick={() => extractContactInfo(formData.resume_text)}
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Auto-fill contact info from resume
                    </Button>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Job Description *</Label>
                  <Textarea 
                    data-testid="input-job-description"
                    placeholder="Paste the job description you're applying for..."
                    className="min-h-[120px] font-mono text-sm"
                    value={formData.job_description}
                    onChange={(e) => handleInputChange("job_description", e.target.value)}
                  />
                </div>

                {/* Special Instructions */}
                <div className="space-y-2">
                  <Label>Special Instructions (Optional)</Label>
                  <Textarea 
                    data-testid="input-special-instructions"
                    placeholder="Any specific directions for your resume? e.g., 'Emphasize leadership experience', 'Focus on technical skills', 'Highlight project management'..."
                    className="min-h-[80px] text-sm"
                    value={formData.special_instructions}
                    onChange={(e) => handleInputChange("special_instructions", e.target.value)}
                  />
                  <p className="text-xs text-gray-500">Tell our AI what to focus on or highlight in your documents.</p>
                </div>

                {/* Work History Section - Structured Input */}
                <div className="p-4 bg-blue-50/50 border border-blue-100 rounded-md space-y-4">
                  <button 
                    type="button"
                    onClick={() => setShowWorkHistory(!showWorkHistory)}
                    className="w-full flex items-center justify-between text-left"
                  >
                    <h4 className="font-medium text-[#071734] flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-[#00a3e0]" /> 
                      Work History (Optional - Add/Edit Jobs)
                    </h4>
                    {showWorkHistory ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </button>
                  <p className="text-xs text-gray-500">Add missing jobs or correct information that wasn't captured from your resume.</p>
                  
                  {showWorkHistory && (
                    <div className="space-y-4">
                      {formData.work_history.map((work, index) => (
                        <div key={work.id} className="p-3 bg-white border border-gray-200 rounded-md space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-[#071734]">Job {index + 1}</span>
                            <button type="button" onClick={() => removeWorkEntry(work.id)} className="text-red-500 hover:text-red-700">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="grid sm:grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-600">Job Title *</Label>
                              <Input 
                                data-testid={`work-title-${index}`}
                                placeholder="e.g., Customer Operations Specialist"
                                value={work.job_title}
                                onChange={(e) => updateWorkEntry(work.id, 'job_title', e.target.value)}
                                className="text-sm"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-600">Company *</Label>
                              <Input 
                                data-testid={`work-company-${index}`}
                                placeholder="e.g., Voya / One America"
                                value={work.company}
                                onChange={(e) => updateWorkEntry(work.id, 'company', e.target.value)}
                                className="text-sm"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-600">Location</Label>
                              <Input 
                                data-testid={`work-location-${index}`}
                                placeholder="e.g., Indianapolis, IN"
                                value={work.location}
                                onChange={(e) => updateWorkEntry(work.id, 'location', e.target.value)}
                                className="text-sm"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="space-y-1">
                                <Label className="text-xs text-gray-600">Start Date</Label>
                                <Input 
                                  data-testid={`work-start-${index}`}
                                  placeholder="Oct 2021"
                                  value={work.start_date}
                                  onChange={(e) => updateWorkEntry(work.id, 'start_date', e.target.value)}
                                  className="text-sm"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs text-gray-600">End Date</Label>
                                <Input 
                                  data-testid={`work-end-${index}`}
                                  placeholder="Present"
                                  value={work.end_date}
                                  onChange={(e) => updateWorkEntry(work.id, 'end_date', e.target.value)}
                                  className="text-sm"
                                />
                              </div>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-gray-600">Key Achievements (one per line)</Label>
                            <Textarea 
                              data-testid={`work-bullets-${index}`}
                              placeholder="• Managed 500+ participant cases...&#10;• Delivered training for new associates...&#10;• Maintained 95%+ customer satisfaction..."
                              value={work.bullets}
                              onChange={(e) => updateWorkEntry(work.id, 'bullets', e.target.value)}
                              className="text-sm min-h-[80px]"
                            />
                          </div>
                        </div>
                      ))}
                      <Button type="button" variant="outline" size="sm" onClick={addWorkEntry} className="w-full border-dashed">
                        <Plus className="w-4 h-4 mr-2" /> Add Job
                      </Button>
                    </div>
                  )}
                </div>

                {/* Education Section - Structured Input */}
                <div className="p-4 bg-green-50/50 border border-green-100 rounded-md space-y-4">
                  <button 
                    type="button"
                    onClick={() => setShowEducation(!showEducation)}
                    className="w-full flex items-center justify-between text-left"
                  >
                    <h4 className="font-medium text-[#071734] flex items-center gap-2">
                      <GraduationCap className="w-4 h-4 text-green-600" /> 
                      Education (Optional - Add/Edit)
                    </h4>
                    {showEducation ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </button>
                  <p className="text-xs text-gray-500">Add missing education or correct information that wasn't captured from your resume.</p>
                  
                  {showEducation && (
                    <div className="space-y-4">
                      {formData.education.map((edu, index) => (
                        <div key={edu.id} className="p-3 bg-white border border-gray-200 rounded-md space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-[#071734]">Education {index + 1}</span>
                            <button type="button" onClick={() => removeEducationEntry(edu.id)} className="text-red-500 hover:text-red-700">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="grid sm:grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-600">Degree/Diploma *</Label>
                              <Input 
                                data-testid={`edu-degree-${index}`}
                                placeholder="e.g., Bachelor of Science in IT"
                                value={edu.degree}
                                onChange={(e) => updateEducationEntry(edu.id, 'degree', e.target.value)}
                                className="text-sm"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-600">Institution</Label>
                              <Input 
                                data-testid={`edu-institution-${index}`}
                                placeholder="e.g., University of Oregon"
                                value={edu.institution}
                                onChange={(e) => updateEducationEntry(edu.id, 'institution', e.target.value)}
                                className="text-sm"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-600">Location</Label>
                              <Input 
                                data-testid={`edu-location-${index}`}
                                placeholder="e.g., Eugene, OR"
                                value={edu.location}
                                onChange={(e) => updateEducationEntry(edu.id, 'location', e.target.value)}
                                className="text-sm"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-600">Year</Label>
                              <Input 
                                data-testid={`edu-year-${index}`}
                                placeholder="e.g., 2020"
                                value={edu.year}
                                onChange={(e) => updateEducationEntry(edu.id, 'year', e.target.value)}
                                className="text-sm"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                      <Button type="button" variant="outline" size="sm" onClick={addEducationEntry} className="w-full border-dashed">
                        <Plus className="w-4 h-4 mr-2" /> Add Education
                      </Button>
                    </div>
                  )}
                </div>

                {/* Career Profiler Fields */}
                {selectedPackage === "career_profiler" && (
                  <div className="p-4 bg-[#ffdd00]/5 border border-[#ffdd00]/30 rounded-md space-y-4">
                    <h4 className="font-medium text-[#071734] flex items-center gap-2">
                      <Star className="w-4 h-4 text-[#ffdd00]" /> Career Profiler Options
                    </h4>
                    <div className="space-y-2">
                      <Label className="text-[#071734]">Target Industry (Optional)</Label>
                      <Input placeholder="e.g., Tech, Healthcare..." value={formData.target_industry} onChange={(e) => handleInputChange("target_industry", e.target.value)} className="border-gray-300" />
                    </div>
                  </div>
                )}

                {/* Style Selection */}
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-md space-y-4">
                  <h4 className="font-medium flex items-center gap-2 text-[#071734]"><Palette className="w-4 h-4 text-[#00a3e0]" /> Resume Style</h4>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                    {colorSchemes.map((s) => (
                      <button key={s.id} type="button" onClick={() => handleInputChange("color_scheme", s.id)}
                        className={`p-2 rounded border text-center ${formData.color_scheme === s.id ? 'border-[#00a3e0] bg-[#00a3e0]/10' : 'border-gray-200 hover:border-[#00a3e0]/50'}`}>
                        <div className="w-6 h-6 rounded-full mx-auto mb-1 border" style={{ backgroundColor: s.primary }} />
                        <span className="text-xs text-[#071734]">{s.name}</span>
                      </button>
                    ))}
                  </div>
                  <div className="grid sm:grid-cols-3 gap-2">
                    {layoutStyles.map((l) => (
                      <button key={l.id} type="button" onClick={() => handleInputChange("layout_style", l.id)}
                        className={`p-2 rounded border text-left ${formData.layout_style === l.id ? 'border-[#00a3e0] bg-[#00a3e0]/10' : 'border-gray-200 hover:border-[#00a3e0]/50'}`}>
                        <span className="text-sm font-medium text-[#071734]">{l.name}</span>
                        <p className="text-xs text-gray-500">{l.description}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Button size="lg" className="w-full bg-[#00a3e0] hover:bg-[#0085b8] text-white" onClick={handleOptimize} disabled={loading}>
              {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Generating...</> : <><Sparkles className="w-5 h-5 mr-2" />Generate My Documents</>}
            </Button>
          </div>
        )}

        {step === 2 && result && (
          <div className="max-w-5xl mx-auto space-y-6">
            {/* Match Score */}
            <div className="grid md:grid-cols-4 gap-4">
              <Card className="text-center border-gray-200">
                <CardContent className="pt-6">
                  <div className="relative w-20 h-20 mx-auto mb-2">
                    <svg className="w-20 h-20 transform -rotate-90">
                      <circle cx="40" cy="40" r="35" stroke="currentColor" strokeWidth="6" fill="transparent" className="text-gray-200" />
                      <circle cx="40" cy="40" r="35" stroke="currentColor" strokeWidth="6" fill="transparent" className="text-[#00a3e0]"
                        strokeDasharray={`${(result.match_score / 100) * 220} 220`} strokeLinecap="round" />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-2xl font-mono font-bold text-[#071734]">{result.match_score}</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500">Match Score</p>
                </CardContent>
              </Card>
              <Card className="md:col-span-3 border-gray-200">
                <CardContent className="pt-6">
                  <h3 className="font-semibold mb-3 text-[#071734]">Keywords</h3>
                  <div className="flex flex-wrap gap-2">
                    {result.keywords_matched.slice(0, 8).map((kw, i) => (
                      <Badge key={i} variant="outline" className="border-[#00a3e0]/50 text-[#00a3e0]">{kw}</Badge>
                    ))}
                    {result.keywords_missing.slice(0, 4).map((kw, i) => (
                      <Badge key={i} variant="outline" className="border-[#ffdd00] text-[#071734] bg-[#ffdd00]/10">{kw}</Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Analysis Preview - Documents generated after payment */}
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle className="text-[#071734]">Analysis Complete</CardTitle>
                <CardDescription className="text-gray-600">Your resume has been analyzed. Complete your purchase to receive your full documents.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-50 p-6 rounded-md space-y-4">
                  {result.fit_summary && (
                    <p className="text-sm text-[#071734]">{result.fit_summary}</p>
                  )}
                  {result.preview_text && (
                    <div className="text-sm text-gray-600 whitespace-pre-line">
                      {result.preview_text}
                    </div>
                  )}
                  <div className="pt-4 border-t border-gray-200">
                    <h4 className="font-medium mb-3 text-[#071734]">What you'll receive after purchase:</h4>
                    <ul className="space-y-2 text-sm text-[#071734]">
                      <li className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-[#00a3e0]" />
                        ATS-Optimized Resume (DOCX)
                      </li>
                      <li className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-[#00a3e0]" />
                        Tailored Cover Letter (DOCX)
                      </li>
                      {(selectedPackage === "complete" || selectedPackage === "career_profiler") && (
                        <>
                          <li className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-[#00a3e0]" />
                            Professional CV (DOCX)
                          </li>
                          <li className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-[#00a3e0]" />
                            Interview Practice Guide (DOCX)
                          </li>
                        </>
                      )}
                      {selectedPackage === "career_profiler" && (
                        <>
                          <li className="flex items-center gap-2">
                            <Star className="w-4 h-4 text-[#ffdd00]" />
                            Career Narrative Profile
                          </li>
                          <li className="flex items-center gap-2">
                            <Star className="w-4 h-4 text-[#ffdd00]" />
                            Job Strategy Guide with Recommendations
                          </li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Checkout Section */}
            <Card className={`${selectedPackage === 'career_profiler' ? 'border-[#ffdd00]' : 'border-[#00a3e0]'}`}>
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                  <div className="space-y-2 text-center md:text-left">
                    <h3 className="text-xl font-heading font-semibold text-[#071734]">{packageNames[selectedPackage]} Package</h3>
                    <div className="flex items-center justify-center md:justify-start gap-3">
                      {getPrice().hasDiscount && (
                        <span className="text-2xl text-gray-400 line-through">${getPrice().original}</span>
                      )}
                      <span className="text-3xl font-mono font-bold text-[#071734]">${getPrice().final}</span>
                      {getPrice().hasDiscount && (
                        <Badge className="bg-green-500 text-white">{getPrice().percent}% OFF</Badge>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto">
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                      <Input 
                        data-testid="input-discount-code"
                        placeholder="Discount code"
                        value={discountCode}
                        onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                        className="w-40 border-gray-300"
                      />
                      <Button 
                        variant="outline" 
                        onClick={validateDiscountCode}
                        disabled={validatingDiscount || !discountCode.trim()}
                        className="border-gray-300"
                      >
                        {validatingDiscount ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                      </Button>
                    </div>
                    
                    {/* Terms Acknowledgment */}
                    <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <input
                        type="checkbox"
                        id="terms-checkbox"
                        checked={agreedToTerms}
                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                        className="mt-1 w-4 h-4 text-[#00a3e0] border-gray-300 rounded focus:ring-[#00a3e0]"
                        data-testid="terms-checkbox"
                      />
                      <label htmlFor="terms-checkbox" className="text-sm text-gray-700">
                        I acknowledge that AI-generated content may contain errors and I will review all documents before use. 
                        I have read and agree to the{" "}
                        <Link to="/terms" target="_blank" className="text-[#00a3e0] hover:underline">Terms of Use</Link>
                        {" "}and{" "}
                        <Link to="/privacy" target="_blank" className="text-[#00a3e0] hover:underline">Privacy Policy</Link>.
                      </label>
                    </div>
                    
                    <Button 
                      data-testid="checkout-btn"
                      size="lg"
                      onClick={handleCheckout}
                      disabled={loading || !agreedToTerms}
                      className={`w-full sm:w-auto ${selectedPackage === 'career_profiler' ? 'bg-[#ffdd00] hover:bg-[#e6c700] text-[#071734] font-semibold' : 'bg-[#00a3e0] hover:bg-[#0085b8] text-white'} ${!agreedToTerms ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Processing...</> : "Complete Purchase"}
                    </Button>
                  </div>
                </div>
                
                {discountValidation && (
                  <div className={`mt-4 p-3 rounded-md ${discountValidation.valid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {discountValidation.message}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default ResumeBuilder;
